import { useCallback, useEffect, useMemo } from "react";
import { logEvent } from "react-migration/lib/util/logEvent";
import { useSnapshot } from "valtio";
import { isPolygonOrMultiPolygon } from "../../geometry_utilities";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { comparablesStore } from "src/js/stores/comparables";
import { IComparablesTransactionDTO, ComparableConnection } from "react-migration/lib/typings/comparables/Comparables";
import { ZooplaComparableConnection, ZooplaComparableDTO } from "react-migration/lib/typings/Zoopla";
import { useClickRadius } from "react-migration/domains/comparables/hooks/useClickRadius";
import { convertPositionsToLatLngCoordinates } from "react-migration/lib/util/geoJson";
import { useQuery } from "@apollo/client";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import COMPARABLE_SEARCH from "react-migration/domains/comparables/apollo/comparableSearch.gql";
import ZOOPLA_COMPARABLE_SEARCH from "react-migration/domains/comparables/apollo/zooplaComparableSearch.gql";
import { COMPARABLES_LIMIT, COMPARABLES_ZOOPLA_LIMIT } from "react-migration/constants";
import { correlateByUPRN } from "react-migration/domains/comparables/util/correlateByUPRN";
import { getZooplaAccessLevel } from "react-migration/domains/comparables/util/getZooplaAccessLevel";
import { filterRecords } from "react-migration/domains/comparables/util/Filters";
import type { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import { PriceTypes } from "react-migration/domains/comparables/typings/PriceTypes";
import { convex } from "@turf/turf";
import { useComparablesLayerContext } from "./ComparableLayerContext/useComparableLayerContext";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { ComparablesState } from "react-migration/domains/comparables/typings/Comparables";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { PriceModes } from "react-migration/domains/comparables/typings/PriceModes";

function hometrackDetailSelection(record: EnrichedComparableDTO): SelectionFeature {
  return {
    type: SelectionType.HOMETRACK_COMPARABLE_TRANSACTION,
    id: record.uprn,
    feature: {
      type: "Feature",
      geometry: record.geometry,
      properties: {
        price: record.price,
        est_price: record.est_price,
        country: record.country,
        zooplaEnrichment: record.zooplaEnrichment,
      },
    },
  };
}

function landTechDetailSelection(record: IComparablesTransactionDTO): SelectionFeature {
  return {
    type: SelectionType.LANDTECH_COMPARABLE_TRANSACTION,
    id: record.transaction_id,
    feature: {
      type: "Feature",
      geometry: record.geometry,
      properties: {
        price: record.price,
        est_price: record.est_price,
        country: record.country,
      },
    },
  };
}

const EMPTY_FILTER = {} as const;

export function useTransactionsAtSelection(
  selection: SelectionFeature,
  onSelection?: (selection: SelectionFeature | undefined) => void,
  customFilters?: ComparablesState["hmlrFilters"] | ComparablesState["hometrackFilters"] | ComparablesState["filters"]
) {
  const { distribution } = useComparablesLayerContext();
  const { dataSource, hmlrFilters, hometrackFilters, priceMode, priceType, RHPdisplayBy } =
    useSnapshot(comparablesStore);

  // TODO: Properly split out filter handling instead of selecting based on data source
  // Select appropriate filters based on data source
  const currentFilters = dataSource === "hmlr" ? hmlrFilters : hometrackFilters;
  const filters = customFilters || currentFilters;

  const clickRadius = useClickRadius();

  const locationFilter = useMemo(() => {
    if (selection.feature?.geometry && isPolygonOrMultiPolygon(selection.feature?.geometry)) {
      const envelope = convex(selection.feature.geometry)?.geometry;
      const coordinates = envelope && convertPositionsToLatLngCoordinates(envelope);
      return coordinates ? { poly_geo_filter: { coordinates } } : EMPTY_FILTER;
    } else if (selection.feature?.geometry?.type === "Point") {
      return {
        radius_geo_filter: {
          location: {
            lat: selection.feature.geometry.coordinates[1],
            lng: selection.feature.geometry.coordinates[0],
          },
          max_dist: clickRadius,
        },
      };
    }
    return EMPTY_FILTER;
  }, [selection, clickRadius]);

  // Feature flag determines execution mode
  const isDualDatasetEnabled = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);

  // Stable sort object to prevent re-render loops
  const sortConfig = useMemo(
    () => ({
      fieldName: "date_of_transfer" as const,
      sortType: {
        order: "desc" as const,
      },
    }),
    []
  );

  // LandTech data query - always fetch LandTech data as base (regardless of priceMode)
  const hasValidLocationFilter = locationFilter && locationFilter !== EMPTY_FILTER;
  const landTechQuery = useQuery<{
    comparableSearch: ComparableConnection;
  }>(COMPARABLE_SEARCH, {
    variables: {
      search: hasValidLocationFilter ? locationFilter : EMPTY_FILTER,
      sort: sortConfig,
      limit: COMPARABLES_LIMIT,
    },
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Gateway,
    },
    skip: !hasValidLocationFilter,
  });

  // Extract UPRNs from LandTech results for Zoopla enrichment
  const uprns = useMemo(() => {
    const edges = landTechQuery.data?.comparableSearch?.edges;
    if (!edges) return [];
    return edges.map((edge) => edge.node.uprn).filter(Boolean) as string[];
  }, [landTechQuery.data?.comparableSearch?.edges]);

  // Zoopla enrichment - only fetch when feature flag is enabled and user has access
  const zooplaAccessLevel = getZooplaAccessLevel();

  const zooplaQuery = useQuery<{
    zooplaComparableSearch: ZooplaComparableConnection;
  }>(ZOOPLA_COMPARABLE_SEARCH, {
    variables: {
      limit: COMPARABLES_ZOOPLA_LIMIT,
      search: hasValidLocationFilter ? locationFilter : EMPTY_FILTER,
    },
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Gateway,
    },
    skip: !isDualDatasetEnabled || !hasValidLocationFilter || zooplaAccessLevel !== "full",
  });

  // Combine and enrich results
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const enrichedResults = useMemo(() => {
    const landTechRecords = landTechQuery.data?.comparableSearch?.edges?.map((edge) => edge.node) || [];

    // Stabilize zooplaQuery.data to prevent infinite re-renders
    const hasZooplaData =
      zooplaQuery.data &&
      Array.isArray(zooplaQuery.data.zooplaComparableSearch?.edges) &&
      zooplaQuery.data.zooplaComparableSearch?.edges?.length > 0;

    if (!isDualDatasetEnabled || !hasZooplaData) {
      // Single dataset mode - just return LandTech data as enriched format
      return landTechRecords.map((record) => ({
        ...record,
        zooplaEnrichment: undefined,
      })) as EnrichedComparableDTO[];
    }

    // Dual dataset mode - correlate LandTech with Zoopla data
    const zooplaRecords = zooplaQuery.data?.zooplaComparableSearch?.edges?.map((edge) => edge.node);

    return correlateByUPRN(landTechRecords, zooplaRecords || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDualDatasetEnabled,
    landTechQuery.data?.comparableSearch?.edges,
    zooplaQuery.data?.zooplaComparableSearch?.edges,
    zooplaQuery.loading,
    // Note: zooplaQuery.data is intentionally omitted to prevent infinite re-renders
  ]);

  const totalCount = enrichedResults.length;

  const selectRecord = useCallback(
    (record: EnrichedComparableDTO | undefined) => {
      if (!record) {
        return;
      }
      logEvent("Comparables Select Record", {
        priceMode,
        priceType: priceType === PriceTypes.PER_UNIT ? "full" : "ppa",
      });
      logEventV2({
        name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
        properties: {
          cardName: "Comparables",
          action: "goTo",
          clickDetail: "Transaction",
        },
      });
      onSelection?.(
        record.zooplaEnrichment?.correlationStatus === "matched" ||
          record.zooplaEnrichment?.correlationStatus === "zoopla_only"
          ? hometrackDetailSelection(record as EnrichedComparableDTO)
          : landTechDetailSelection(record as IComparablesTransactionDTO)
      );
    },

    [priceMode, priceType, onSelection]
  );

  const { includedResults } = useMemo(() => {
    const resultsExcludingSelection = enrichedResults.filter(({ transaction_id }) => {
      // Include Zoopla-only records (empty transaction_id)
      // or records that don't match the current selection
      const isComparableSelection =
        selection.type === SelectionType.HOMETRACK_COMPARABLE_TRANSACTION ||
        selection.type === SelectionType.LANDTECH_COMPARABLE_TRANSACTION;

      // When in the US and using ReGrid data the transaction and the selection id (ownership_id) are the same.
      // The original code here resulted in on site comparables being filtered out when ReGrid data source is used
      // We may be able to change this isComparableSelection to isUSUser once ReGrid is out to simplify this.
      return (
        transaction_id === "unknown" || !isComparableSelection || (transaction_id && transaction_id !== selection?.id)
      );
    });

    const filteredResults = filterRecords(filters, resultsExcludingSelection, dataSource, RHPdisplayBy as PriceModes);

    return filteredResults;
  }, [enrichedResults, filters, dataSource, RHPdisplayBy, selection?.id, selection.type]);

  // Determine loading and error states
  const loading =
    landTechQuery.loading ||
    (isDualDatasetEnabled && zooplaAccessLevel === "full" && uprns.length > 0 && zooplaQuery.loading);
  const error = landTechQuery.error;

  const zooplaResults = useMemo(
    () =>
      enrichedResults
        .filter(
          (result) =>
            result.zooplaEnrichment?.correlationStatus === "matched" ||
            result.zooplaEnrichment?.correlationStatus === "zoopla_only"
        )
        .map((result) => ({
          uprn: result.uprn!,
          attributes: result.zooplaEnrichment!.attributes,
          valuation: result.zooplaEnrichment!.valuation,
        })) as ZooplaComparableDTO[],
    [enrichedResults]
  );

  // Log no match events (maintaining compatibility with existing analytics)
  useEffect(() => {
    if (enrichedResults.length === 0 && !loading && hasValidLocationFilter) {
      logEvent("Comparables Hometrack No Match", {
        search: { ...locationFilter },
        priceMode,
      });
    }
  }, [enrichedResults.length, loading, hasValidLocationFilter, locationFilter, priceMode]);

  return {
    totalCount,
    includedResults,
    loading,
    error,
    distribution,
    priceMode,
    selectRecord,
    enrichedResults,
    zooplaResults,
  };
}
